import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Popover, Spin, Tooltip } from 'antd';
import moment from 'moment';
import { findIndex, groupBy, map, sumBy, sortBy } from 'lodash';

import Box from '../../../../../../../assets/icons/open-box.jpg';
import Print from '../../../../../../../assets/icons/print-icon.svg';

import ProductImg from "../../../../../../../assets/images/newImages/nestle.svg";

import { StepTwoCard } from '../../style';
import productLabel from '../../../../../../../utils/print-labels/productLabel';
import { isValidFnsku, notifyMissingFnsku } from './packNowLabelHelpers';

const Card = ({
  noActions,
  cardData,

  setProductInfo,
  setItemIndex,
  setItemShipments,
  activeShipmentIndex,
  setItemShipmentIndex,
  setProductDetail,
  packingMethod,
  skuGroupMap,
}) => {

  const itemsPerPage = 20;

  const {
    shipmentDetails,
    fetchingWarehouseShipments,
    settingPackInformation,
  } = useSelector((state) => state.postShipment || {});

  const uniqueGroupIds = [...new Set(Object.values(skuGroupMap || {}))].filter(Boolean).sort();
  const groupLabelMap = {};
  uniqueGroupIds.forEach((id, i) => { groupLabelMap[id] = `Group ${i + 1}`; });

  const [mergedData, setMergedData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [loadedItems, setLoadedItems] = useState(itemsPerPage);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!cardData?.length) {
      setMergedData([]);
      return;
    };

    const data = map(groupBy(cardData, "msku"), (group) => ({
      ...group[0],
      quantity: sumBy(group, (item) => Number(item?.quantity || 0)),
      boxed_quantity: sumBy(group, (item) => Number(item?.boxed_quantity || 0)),
    }));

    const sortedData = data.sort((a, b) => {
      const aRemaining = Number(a?.quantity || 0) - Number(a?.boxed_quantity || 0);
      const bRemaining = Number(b?.quantity || 0) - Number(b?.boxed_quantity || 0);

      const aComplete = aRemaining === 0;
      const bComplete = bRemaining === 0;

      if (aComplete && !bComplete) return 1;
      if (!aComplete && bComplete) return -1;

      return Number(b?.quantity || 0) - Number(a?.quantity || 0);
    });

    setMergedData(sortedData);
    setLoadedItems(itemsPerPage);
    setHasMore(sortedData.length > itemsPerPage);
  }, [cardData]);

  useEffect(() => {
    if (!mergedData.length) {
      setDisplayedData([]);
      return;
    }

    const slicedData = mergedData.slice(0, loadedItems);
    setDisplayedData(slicedData);
    setHasMore(loadedItems < mergedData.length);
  }, [mergedData, loadedItems]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      const nextLoadedItems = Math.min(loadedItems + itemsPerPage, mergedData.length);
      setLoadedItems(nextLoadedItems);
    }
  }, [hasMore, loadedItems, mergedData.length]);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleGenerateProductLabels = (item, index, numberOfLabels) => {
    const {
      quantity,
      min_bundle_quantity = 1,
      item_name,
      prep_instructions,
      expiration,
      fnsku,
      item_condition = '11'
    } = item || {};

    if (!isValidFnsku(fnsku)) {
      notifyMissingFnsku(item_name || item?.msku);
      return;
    }

    const labelsData = {
      fnsku,
      title: item_name || '',
      expiryDate: expiration ? moment(expiration).format('MM-DD-YYYY') : 'N/A',
      bundleQty: min_bundle_quantity,
      numberOfLabels,
      username: "Opulent",
      prepInstruction: prep_instructions,
      item_condition
    };

    productLabel(labelsData);
  };

  const ViewIdentifierContent = ({ identifiersList }) => {
    return (
      <StepTwoCard>
        <header>
          <p>Type</p>
          <p>Value</p>
        </header>
        <div className="items-ui-view-wrapper">
          <div className="items-ui-view-items">
            {identifiersList?.map((identifier, index) => (
              <React.Fragment key={index}>
                <p>{identifier?.identifier_type?.toUpperCase()}</p>
                <p>{identifier?.identifier_value?.toUpperCase()}</p>
              </React.Fragment>
            ))}
          </div>
        </div>
      </StepTwoCard>
    );
  };

  return (
    <Spin tip='Loading...' spinning={settingPackInformation}>
      <StepTwoCard>
        <div 
          className='tab-cards-overlay-wrapper'
          ref={scrollContainerRef}
          style={{
            maxHeight: 'inherit',
            overflowY: 'auto',
            paddingRight: '8px'
          }}
        >
          {displayedData.map((item, index) => (
            <div key={index} className={`tab-cards-item-wrapper ${item?.boxed_quantity == item?.quantity ? 'boxed-item' : ''}`}>
              <div className='title'>
                <img src={item.image_url || ProductImg} />
                <div className='heading'>
                  <div className='title-info-ui-wrap'>
                    <p>{item.item_name}</p>
                    <Popover overlayClassName='view-identify-view-wrapper' placement='top' content={
                      <ViewIdentifierContent
                        identifiersList={item?.product_details || []}
                      />}>
                      <p className='text-items-wrapper-link'>View Identifiers</p>
                    </Popover>
                  </div>
                  <div className='title-items'>
                    <div className='card-items'>
                      <p>SKU:</p>
                      <p>{item?.msku}</p>
                    </div>
                  
                    <div className='card-items'>
                      <p>FNSKU:</p>
                      <p>{item.fnsku}</p>
                    </div>
                    <div className='card-items'>
                      <p>Total QTY:</p>
                      <p>{item?.quantity}</p>
                    </div>
                    <div className='card-items'>
                      <p>Total Boxed QTY:</p>
                      <p>{item?.boxed_quantity || 0}</p>
                    </div>
                    {packingMethod === 'pack_now' && skuGroupMap && skuGroupMap[item?.msku] && (
                      <div className='card-items' style={{ marginTop: 4 }}>
                        <p>Packing Group:</p>
                        <p>
                          <span style={{
                            background: '#E3F2FD',
                            color: '#1565D8',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontSize: 12,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                          }}>
                            {groupLabelMap[skuGroupMap[item?.msku]] || `Group 1`}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {
                !noActions &&
                <div className='actions'>
                  <Tooltip title='Add To Box'>
                    <img onClick={() => {
                      setProductDetail(item);
                      setProductInfo(true);

                      const filteredShipments = (activeShipmentIndex === -1
                        ? shipmentDetails.shipments
                        : [shipmentDetails.shipments[activeShipmentIndex]]
                      )
                        .filter(({ shipment_items_info }) => shipment_items_info?.some(({ msku = '' }) => msku === item?.msku))
                        ?.map(({ shipment_items_info, boxes_info, ...shipmentDetails }) => {
                          const { boxed_quantity = 0, quantity, id: shipment_item_id } = shipment_items_info?.find(({ msku = '' }) => msku === item?.msku) || {};

                          return {
                            ...shipmentDetails,
                            boxed_quantity,
                            quantity,
                            shipment_item_id
                          };
                        });


                      const availableShipmentIndex = filteredShipments.findIndex(
                        ({ quantity, boxed_quantity }) => (Number(quantity) || 0) - (boxed_quantity || 0) >= 0
                      );
                      const itemIndex = findIndex(cardData, ({ id }) => id === filteredShipments[availableShipmentIndex]?.shipment_item_id);
                      setItemIndex(itemIndex);
                      setItemShipments(filteredShipments);

                      if (activeShipmentIndex === -1) {
                        setItemShipmentIndex(availableShipmentIndex);
                      }
                      else {
                        const { amazon_shipment_id } = shipmentDetails?.shipments[activeShipmentIndex] || {};
                        setItemShipmentIndex(filteredShipments.findIndex((shipment) => shipment?.amazon_shipment_id === amazon_shipment_id));
                      }
                    }} src={Box} alt="Box" />
                  </Tooltip>
                  <Tooltip title='Print'>
                    <img src={Print} alt="Print"
                      onClick={() => handleGenerateProductLabels(item, index, item?.quantity)}
                    />
                  </Tooltip>
                </div>
              }
            </div>
          ))}
          {hasMore && displayedData.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              color: '#666'
            }}>
              <span>Scroll down to load more items...</span>
            </div>
          )}
          {!cardData?.length && <p style={{
            display: 'flex',
            justifyContent: 'space-evenly',
            marginTop: '60px'
          }}>No Data Found</p>
          }
          {!hasMore && displayedData.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              color: '#999',
              fontSize: '14px'
            }}>
              <span>All items loaded ({displayedData.length} total)</span>
            </div>
          )}
        </div>
      </StepTwoCard>
    </Spin>
  )
}

export default Card